import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bookmark, Check, Heart, Star, LogOut, Clapperboard } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useMyCinema } from "../context/MyCinemaContext";
import { useTranslate } from "../context/TranslateContext";
import { getMovie } from "../mock/data";

export default function MyCinema() {
  const { user, data, signOut, setAuthOpen } = useMyCinema();
  const { t } = useTranslate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
        <InnerNav />
        <div className="mx-auto max-w-lg px-6 py-32 text-center">
          <Clapperboard className="mx-auto h-12 w-12 text-gold" />
          <h1 className="mt-6 font-display text-4xl font-semibold text-cream">{t("Your cinema, your way")}</h1>
          <p className="mt-3 font-body text-ash">{t("Sign in to build watchlists, rate films and keep your cinema memories.")}</p>
          <button onClick={() => setAuthOpen(true)} data-testid="mycinema-signin"
            className="mt-8 rounded-full bg-gold px-8 py-3 font-body font-medium text-ink transition-transform hover:scale-105">{t("Sign in")}</button>
        </div>
      </div>
    );
  }

  const ratingIds = Object.keys(data.ratings || {});
  const empty = !data.watchlist.length && !data.watched.length && !data.favorites.length && !ratingIds.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Overline>My Cinema</Overline>
            <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">
              {t("Welcome,")} <span className="italic text-gradient-gold">{user.name.split(" ")[0]}</span>
            </h1>
          </div>
          <button onClick={signOut} data-testid="signout"
            className="inline-flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-body text-sm text-ash hover:border-crimson hover:text-crimson">
            <LogOut className="h-4 w-4" /> {t("Sign out")}
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat icon={Bookmark} n={data.watchlist.length} label="Watchlist" color="#D4AF37" />
          <Stat icon={Check} n={data.watched.length} label="Watched" color="#2FA8A0" />
          <Stat icon={Heart} n={data.favorites.length} label="Favourites" color="#C0392B" />
          <Stat icon={Star} n={ratingIds.length} label="Rated" color="#E8873A" />
        </div>

        {empty && <p className="mt-16 text-center font-body text-ash" data-testid="mycinema-empty">{t("Nothing here yet. Explore films and start building your collection.")}</p>}

        <Shelf title="Watchlist" ids={data.watchlist} testId="shelf-watchlist" />
        <Shelf title="Watched" ids={data.watched} testId="shelf-watched" />
        <Shelf title="Favourites" ids={data.favorites} testId="shelf-favorites" />

        {ratingIds.length > 0 && (
          <div className="mt-16">
            <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t("My Ratings")}</h2>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
              {ratingIds.map((id) => { const m = getMovie(id); if (!m) return null; return (
                <Link key={id} to={`/movie/${id}`} data-testid={`rated-${id}`}>
                  <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-xl border border-edge">
                    <div className="relative aspect-[2/3] overflow-hidden">
                      <img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-paper/90 px-2 py-0.5">
                        <Star className="h-3 w-3 fill-gold text-gold" /><span className="font-body text-xs text-cream">{data.ratings[id]}/10</span>
                      </div>
                    </div>
                    <div className="p-3"><div className="font-display text-base text-cream">{m.title}</div></div>
                  </motion.div>
                </Link>
              ); })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, n, label, color }) => {
  const { t } = useTranslate();
  return (
    <div className="rounded-2xl border border-edge bg-surface/40 p-5">
      <Icon className="h-6 w-6" style={{ color }} />
      <div className="mt-3 font-display text-4xl font-semibold text-cream">{n}</div>
      <div className="font-body text-xs uppercase tracking-widest text-ash">{t(label)}</div>
    </div>
  );
};

const Shelf = ({ title, ids, testId }) => {
  const { t } = useTranslate();
  if (!ids.length) return null;
  return (
    <div className="mt-16" data-testid={testId}>
      <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t(title)}</h2>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
        {ids.map((id) => { const m = getMovie(id); if (!m) return null; return (
          <Link key={id} to={`/movie/${id}`}>
            <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-xl border border-edge">
              <div className="aspect-[2/3] overflow-hidden">
                <img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-3">
                <div className="font-display text-base text-cream">{m.title}</div>
                <div className="font-body text-xs text-ash">{m.language} · {m.year}</div>
              </div>
            </motion.div>
          </Link>
        ); })}
      </div>
    </div>
  );
};
